import { i18n } from '@/locales'
import _ from 'lodash'
import moment from 'moment-timezone'

const ohlc = {
  o: 0,
  l: 0,
  h: 0,
  c: 0,
  v: 0,
}
const CHANGE_FROM_LAST_TICK = true

const market = {
  namespaced: true,
  state: {
    platform: 'MOEX',
    platforms: [{ name: 'MOEX', id: 1, imgName: 'ru.svg' }], // platforms for current ticker
    allPlatforms: [{ name: 'MOEX', id: 1, imgName: 'ru.svg' }], // all platforms in backoffice
    ticker: '',
    tickerType: '',
    tickerTypeVisible: false,
    companyName: 'loading',
    companyDescription: 'loading',
    trading: true,
    openTime: '11:00',
    closeTime: '18:00',
    marketTz: 'Europe/Moscow',
    currentPrice: 0,
    lastPrice: 0,
    fundamentalPrice: 0,
    lastDayPrice: 0,
    metrics3m: ohlc,
    metrics52w: ohlc,
    metricsDay: ohlc,
    marketCapNum: 0,
    adrCoeff: 1,
    digitsAfterDot: 2,
    initialized: false,
    currency: 'RUB',
  },
  mutations: {
    updateTicker(state, ticker) {
      state.ticker = ticker.toUpperCase()
    },
    updateMarket(state, market) {
      state.platform = market.toUpperCase()
    },
    updateAllPlatforms(state, platforms) {
      state.allPlatforms = platforms.map((el) => ({
        name: el.Name || el.name,
        id: el.id,
        imgName: el.imgName,
      }))
    },
    updatePlatforms(state, platforms) {
      state.platforms = platforms
    },
    updateCurrentPrice(state, price) {
      if (CHANGE_FROM_LAST_TICK) {
        if (price !== state.currentPrice || !state.initialized) {
          state.lastPrice = state.currentPrice
          state.currentPrice = state.metricsDay.c = state.metrics3m.c = state.metrics52w.c = price
        }
        if (!state.initialized) {
          state.lastPrice = state.currentPrice
        }
        state.initialized = true
      } else {
        state.lastPrice = state.currentPrice
        state.currentPrice = state.metricsDay.c = state.metrics3m.c = state.metrics52w.c = price
      }
    },
    updateDay(state, { o, h, l, c }) {
      state.metricsDay = { o, h, l, c }
    },
    recomputeMetrics(state) {
      // state.metricsDay.l = _.min([state.currentPrice, state.metricsDay.l])
      // state.metricsDay.h = _.max([state.currentPrice, state.metricsDay.h])
      // except day prices
      state.metrics3m.l = _.min([state.currentPrice, state.metrics3m.l])
      state.metrics3m.h = _.max([state.currentPrice, state.metrics3m.h])
      state.metrics52w.l = _.min([state.currentPrice, state.metrics52w.l])
      state.metrics52w.h = _.max([state.currentPrice, state.metrics52w.h])
    },
    updateLastDayPrice(state, price) {
      state.lastDayPrice = price
    },
    updateDigitsAfterDot(state, digits) {
      if (_.isNumber(digits)) state.digitsAfterDot = digits
      else state.digitsAfterDot = 2
    },
    updateMetrics(state, { metrics52w, metrics3m }) {
      let errored = false
      ;[('o', 'h', 'l', 'c')].forEach((key) => {
        if (!_.has(metrics52w, key) || !_.has(metrics3m, key)) errored = false
      })
      if (errored) {
        console.log('Wrong params in market/updateMetrics')
        return
      }
      state.metrics52w = metrics52w
      state.metrics3m = metrics3m
    },
    update(state, params) {
      if (!_.isObject(params)) {
        console.log('Wrong market/update mutation params', params)
        return
      }
      //platform, ticker, status, price, metrics
      // [
      //   'platform',
      //   'ticker',
      //   'status',
      //   'price',
      //   'metrics3m',
      //   'metrics52w',
      //   'metricsDay',
      //   'capitalization',
      // ],
      _.forEach(_.keys(params), (key) => {
        if (_.isObject(params[key]))
          state[key] = _.assign(state[key], params[key])
        else state[key] = params[key]
      })
    },
    refreshTimer(state) {
      const open = moment.tz(state.openTime, 'HH:mm', state.marketTz),
        close = moment.tz(state.closeTime, 'HH:mm', state.marketTz)
      state.trading = !(open.isAfter() || close.isBefore())
    },
  },
  actions: {
    updateAll({ dispatch }) {
      return dispatch('updateMarketInfo').then(() =>
        Promise.all([
          dispatch('updateLastDay'),
          dispatch('updateMetrics'),
          dispatch('updateTicker'),
        ])
      )
    },
    updateMarketInfo({ state, commit, dispatch }) {
      return dispatch(
        'request/getMarketInfo',
        { market: state.platform },
        { root: true }
      ).then((res) => {
        commit('update', {
          openTime: res.start,
          closeTime: res.end,
          // TODO: change to tzOffset
          marketTz: res.tz,
        })
      })
    },
    getPlatforms({ commit, dispatch }) {
      return dispatch('request/getMarkets', {}, { root: true })
        .then((res) => {
          commit('updateAllPlatforms', res)
        })
        .catch((err) => {
          dispatch(
            'layout/showMessage',
            {
              title: 'Error',
              message: `Getting platforms ${err}`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
    updateLastDay({ state, commit, dispatch }) {
      dispatch(
        'request/getMarketLastDay',
        { market: state.platform, ticker: state.ticker },
        { root: true }
      )
        .then((res) => {
          commit('updateLastDayPrice', _.get(res, 'last', 0))
        })
        .catch((err) => {
          dispatch(
            'layout/showMessage',
            {
              title: 'Error',
              message: `Updating last day price ${err}`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
    updateMetrics({ state, commit, dispatch }) {
      dispatch(
        'request/getMarketMetrics',
        { market: state.platform, ticker: state.ticker },
        {
          root: true,
        }
      )
        .then((res) => {
          commit('updateMetrics', {
            metrics52w: {
              o: _.get(res, '_52w.open', 0),
              h: _.get(res, '_52w.high', 0),
              l: _.get(res, '_52w.low', 0),
              c: _.get(res, '_52w.close', 0),
              v: _.get(res, '_52w.volume', 0),
            },
            metrics3m: {
              o: _.get(res, '_3m.open', 0),
              h: _.get(res, '_3m.high', 0),
              l: _.get(res, '_3m.low', 0),
              c: _.get(res, '_3m.close', 0),
              v: _.get(res, '_3m.volume', 0),
            },
          })
        })
        .catch((err) => {
          dispatch(
            'layout/showMessage',
            {
              title: 'Error',
              message: `Get market metrics ${err}`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
    updateTicker({ state, dispatch, commit }) {
      return dispatch(
        'request/getTickerMarketInfo',
        {
          ticker: state.ticker,
          market: state.platform,
        },
        { root: true }
      )
        .then((res) => {
          commit('update', {
            tickerType: res.tickerType || 'comm_stock',
            tickerTypeVisible:
              res.tickerTypeVisible === null ? false : res.tickerTypeVisible,
            ...(res.ticker_id && {
              companyName:
                res.ticker_id[`companyName_${i18n.locale}`] ||
                res.ticker_id.companyName,
              companyDescription:
                res.ticker_id[`description_${i18n.locale}`] ||
                res.ticker_id.description,
            }),
            currency: res.currency || 'RUB',
            marketCapNum: res.marketCapNum && Number(res.marketCapNum),
            adrCoeff: res.adrCoeff || 1,
            fundamentalPrice: res.fundamentalPrice,
          })

          commit(
            'ticker/update',
            {
              priceUnderReview: res.priceUnderReview || false,
            },
            { root: true }
          )
          commit('updateDigitsAfterDot', res.digitsAfterDot)

          commit(
            'ticker/update',
            {
              dividents: res.dividents,
              ideas: res.ideas,
              articles: res.articles,
              multipliers: res.multipliers,
              mainMult: res.mainMultiplier,
              ...(res.chartComparables && {
                chartComparables: res.chartComparables,
              }),
              ...(res[`preview_${i18n.locale}`] && {
                previewImgUrl: `${process.env.VUE_APP_BACKOFFICE_URL}${res[
                  `preview_${i18n.locale}`
                ].url.slice(1, 999)}`,
              }),
            },
            { root: true }
          )

          if (res.ticker_id) {
            return dispatch(
              'request/getTickerInfo',
              { id: res.ticker_id.id },
              { root: true }
            ).then((tickerRes) => {
              if (_.isArray(tickerRes.tickerMarketInfos)) {
                const platforms = state.allPlatforms.reduce((acc, p) => {
                  const tmis = tickerRes.tickerMarketInfos.filter(
                    (tmi) => p.id === tmi.market && tmi.disabled !== true
                  )
                  if (tmis.length < 1) return acc

                  acc.push({
                    ...p,
                    fullName: tmis[0].name,
                  })
                  return acc
                }, [])
                commit('updatePlatforms', platforms)
              }
              commit(
                'ticker/update',
                {
                  sector:
                    _.get(
                      tickerRes,
                      `sector.SectorName_${i18n.locale}`,
                      false
                    ) || _.get(tickerRes, 'sector.SectorName', 'default'),
                },
                { root: true }
              )
              commit(
                'ticker/update',
                {
                  events: tickerRes.events,
                },
                { root: true }
              )
            })
          } else {
            dispatch(
              'layout/showMessage',
              {
                title: 'Error',
                message: `No ticker info for TMI ${state.ticker} ${state.platform}`,
                type: 'error',
              },
              { root: true }
            )
          }
        })
        .catch((err) => {
          dispatch(
            'layout/showMessage',
            {
              title: 'Error',
              message: `Get ticker info ${err}`,
              type: 'error',
            },
            { root: true }
          )
        })
    },
  },
  getters: {
    change(state) {
      // change from last day
      if ((_.isNumber(state.lastDayPrice), _.isNumber(state.currentPrice))) {
        return state.currentPrice - state.lastDayPrice
      }
      return 0
    },
    changePercent(state, getters) {
      // change from last day
      if (
        _.isNumber(getters.change) &&
        _.isNumber(state.lastDayPrice) &&
        state.lastDayPrice !== 0
      ) {
        return Number(((getters.change / state.lastDayPrice) * 100).toFixed(2))
      }
      return 0
    },
    // change(state) {
    //   // change from last tick
    //   if ((_.isNumber(state.lastPrice), _.isNumber(state.currentPrice))) {
    //     return state.currentPrice - state.lastPrice
    //   }
    //   return 0
    // },
    // changePercent(state, getters) {
    //   if ((_.isNumber(getters.change), _.isNumber(state.lastPrice))) {
    //     return Number(((getters.change / state.lastPrice) * 100).toFixed(2))
    //   }
    //   return 0
    // },
    changeFundPercent(state) {
      if (
        _.isNumber(state.fundamentalPrice) &&
        _.isNumber(state.currentPrice)
      ) {
        return Math.round(
          (state.fundamentalPrice / state.currentPrice - 1) * 100
        )
      }
      return 0
    },
    platformId(state) {
      const f = state.allPlatforms.filter((el) => el.name === state.platform)
      return f.length > 0 ? f[0].id : 1
    },
  },
}

export default market
